'use client';

import { c as _c } from "react/compiler-runtime";
import { type RewardItem } from '@shared/all/domains/products';
import { unwrapFromResult } from '@shared/utils/helpers/resultWrapper';
import { useQuery } from '@tanstack/react-query';
import { BadgeCheck, CheckCircle, ChevronDown, ChevronUp, Zap } from 'lucide-react';
import { useState } from 'react';
import { client } from '../../utils/trpc';
import Error from '../common/Error';
const EarnTrons = () => {
  const $ = _c(20);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      queryKey: ["getRewards"],
      queryFn: _temp
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    isLoading,
    data,
    error
  } = useQuery(t0);
  const verifiedHuman = data?.verifiedHuman ?? false;
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <h3 className="font-semibold text-textPrimary">Earn Trons</h3>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== verifiedHuman) {
    t2 = verifiedHuman && <span className="flex items-center gap-1 rounded-full bg-green-500/15 px-2 py-0.5 text-xs font-semibold text-green-500"><BadgeCheck size={13} /> Verified human</span>;
    $[2] = verifiedHuman;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2) {
    t3 = <div className="mb-2 ml-1 flex items-center gap-2">{t1}{t2}</div>;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== data || $[7] !== verifiedHuman) {
    t4 = !verifiedHuman && data && <div className="mb-3 rounded-lg border border-amber-500/40 bg-amber-500/5 p-3"><p className="text-sm font-semibold text-textPrimary">Verify you're human to earn trons</p><p className="mt-1 text-xs text-textSecondary">These rewards only pay out to verified humans — it's what keeps bots from farming free AI. Verification takes a moment on your phone: it uses your device biometrics, and your data never leaves it.</p><a href="/biometric-verification" className="mt-2 inline-block rounded-md bg-primary px-3 py-1.5 text-sm font-semibold text-white">Verify as human</a></div>;
    $[6] = data;
    $[7] = verifiedHuman;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== data?.rewards || $[10] !== verifiedHuman) {
    t5 = data?.rewards.map(reward => <EarnTronItem key={reward.id} item={reward} locked={!verifiedHuman} />);
    $[9] = data?.rewards;
    $[10] = verifiedHuman;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const t6 = Boolean(error);
  let t7;
  if ($[12] !== isLoading || $[13] !== t6) {
    t7 = <Error loading={isLoading} error={t6} />;
    $[12] = isLoading;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t3 || $[16] !== t4 || $[17] !== t5 || $[18] !== t7) {
    t8 = <div className="flex w-full flex-col px-4">{t3}{t4}{t5}{t7}</div>;
    $[15] = t3;
    $[16] = t4;
    $[17] = t5;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  return t8;
};
const EarnTronItem = t0 => {
  const $ = _c(30);
  const {
    item,
    locked
  } = t0;
  const [isOpen, setIsOpen] = useState(false);
  const t1 = `mb-2 flex w-full flex-col rounded-lg border ${item.completed ? "border-green-500/40 bg-green-500/5" : "border-ds-border-standard"} ${locked ? "opacity-50" : ""}`;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => setIsOpen(_temp2);
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== item.completed) {
    t3 = item.completed ? <CheckCircle size={20} className="shrink-0 text-green-500" /> : <Zap size={20} className="shrink-0" />;
    $[1] = item.completed;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== item.quantity) {
    t4 = item.quantity.toLocaleString();
    $[3] = item.quantity;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t4) {
    t5 = <p className="text-sm font-bold">{t4} Trons</p>;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== item.title) {
    t6 = <p className="text-xs text-textSecondary">{item.title}</p>;
    $[7] = item.title;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t5 || $[10] !== t6) {
    t7 = <div>{t5}{t6}</div>;
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t3 || $[13] !== t7) {
    t8 = <div className="flex items-center gap-3">{t3}{t7}</div>;
    $[12] = t3;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== item.completed) {
    t9 = item.completed && <span className="mr-2 rounded-full bg-green-500/15 px-2 py-0.5 text-xs font-semibold text-green-500">Earned</span>;
    $[15] = item.completed;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== isOpen) {
    t10 = isOpen ? <ChevronUp size={16} className="text-textSecondary" /> : <ChevronDown size={16} className="text-textSecondary" />;
    $[17] = isOpen;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== t10 || $[20] !== t8 || $[21] !== t9) {
    t11 = <div className="flex w-full cursor-pointer select-none items-center justify-between p-3" onClick={t2}>{t8}{t9}{t10}</div>;
    $[19] = t10;
    $[20] = t8;
    $[21] = t9;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== isOpen || $[24] !== item.description) {
    t12 = isOpen && <div className="px-3 pb-3 text-xs text-textSecondary"><p>{item.description}</p></div>;
    $[23] = isOpen;
    $[24] = item.description;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== t1 || $[27] !== t11 || $[28] !== t12) {
    t13 = <div className={t1}>{t11}{t12}</div>;
    $[26] = t1;
    $[27] = t11;
    $[28] = t12;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  return t13;
};
export default EarnTrons;
function _temp() {
  return unwrapFromResult(client.products.tronRewards.query());
}
function _temp2(prev) {
  return !prev;
}